/**
 * Sentry — Error Tracking & Performance Monitoring
 *
 * Initializes Sentry for the Origin platform.
 * Captures unhandled errors, rejected promises, and console.error calls.
 * Enables performance tracing for route changes and API calls.
 *
 * Environment variable:
 *   VITE_SENTRY_DSN — Sentry Data Source Name (from sentry.io project settings)
 *
 * @see PRD §25 — Observability & Monitoring
 */

import * as Sentry from '@sentry/react'

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined

/**
 * Initialize Sentry. Call once in main.tsx before rendering.
 * No-ops gracefully if VITE_SENTRY_DSN is not set.
 */
export function initSentry(): void {
  if (!SENTRY_DSN) {
    return
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE, // 'development' | 'production'
    release: `phyre@${import.meta.env.VITE_APP_VERSION ?? '0.0.0'}`,

    // Performance monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,

    // Session replay for error reproduction
    replaysSessionSampleRate: 0, // Don't record all sessions
    replaysOnErrorSampleRate: 1.0, // Record 100% of sessions with errors

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false, // Salon names aren't PII
        blockAllMedia: false,
      }),
    ],

    // Filter out noisy errors
    beforeSend(event) {
      // Skip ResizeObserver loop errors (browser noise)
      if (event.exception?.values?.[0]?.value?.includes('ResizeObserver loop')) {
        return null
      }
      // Skip WebGL context lost (expected on mobile)
      if (event.exception?.values?.[0]?.value?.includes('WebGL')) {
        return null
      }
      return event
    },

    // Add tenant context
    beforeBreadcrumb(breadcrumb) {
      // Redact auth tokens from breadcrumbs
      if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
        if (breadcrumb.data?.url?.includes('token')) {
          breadcrumb.data.url = '[REDACTED]'
        }
      }
      return breadcrumb
    },
  })
}

/**
 * Set user context for Sentry (call after login).
 */
export function setSentryUser(
  user: { id: string; email?: string; tenantId?: string } | null
): void {
  if (!SENTRY_DSN) return

  if (user) {
    Sentry.setUser({ id: user.id, email: user.email })
    if (user.tenantId) {
      Sentry.setTag('tenant_id', user.tenantId)
    }
  } else {
    Sentry.setUser(null)
  }
}

/**
 * Manually capture an error with optional extra context.
 */
export function captureError(error: Error, context?: Record<string, unknown>): void {
  if (!SENTRY_DSN) {
    console.error('[Sentry] Would capture:', error, context)
    return
  }

  Sentry.captureException(error, { extra: context })
}

/**
 * Create a Sentry ErrorBoundary wrapper (use in place of or alongside the existing ErrorBoundary).
 */
export const SentryErrorBoundary = SENTRY_DSN
  ? Sentry.ErrorBoundary
  : ({ children }: { children: React.ReactNode; fallback?: React.ReactNode }) => children

// ─── Performance Monitoring Utilities ──────────────────────────────

/**
 * Place a named performance mark with a Sentry breadcrumb.
 * Use in useEffect hooks to track component lifecycle events.
 *
 * @example
 * ```ts
 * useEffect(() => {
 *   markPerformance('booking-page-mount')
 *   return () => markPerformance('booking-page-unmount')
 * }, [])
 * ```
 */
export function markPerformance(name: string): void {
  try {
    performance.mark(name)
  } catch {
    // performance.mark may throw in SSR or Web Workers
  }

  if (!SENTRY_DSN) return
  Sentry.addBreadcrumb({
    category: 'performance',
    message: name,
    level: 'info',
  })
}

/**
 * Measure duration between two named marks and record as a Sentry span.
 *
 * @example
 * ```ts
 * markPerformance('data-fetch-start')
 * await fetchData()
 * markPerformance('data-fetch-end')
 * measurePerformance('data-fetch', 'data-fetch-start', 'data-fetch-end')
 * ```
 */
export function measurePerformance(name: string, startMark: string, endMark: string): void {
  try {
    performance.measure(name, startMark, endMark)
    const entries = performance.getEntriesByName(name, 'measure')
    const measure = entries[entries.length - 1]

    if (!measure) return

    if (SENTRY_DSN) {
      Sentry.addBreadcrumb({
        category: 'performance',
        message: `${name}: ${measure.duration.toFixed(1)}ms`,
        level: 'info',
        data: {
          duration: measure.duration,
          startTime: measure.startTime,
        },
      })
    }
  } catch {
    // Silently ignore if marks don't exist
  }
}
